
export default {
  name: "HexCompany",
  props: {
    companies: { type: Array, required: true },
    showAllStatuses: { type: Boolean, default: false },
  },
  data() {
    return {
      cols: 4,
      gap: 4,
      hexWidth: 160,
      scale: 1,
      hoverIndex: null,
      hoverTimer: null,
      resizeTimeout: null as any,
      isInitialized: false,
    };
  },
  computed: {
    hexHeight() {
      return Math.floor((this.hexWidth * this.scale * 2) / Math.sqrt(3));
    },
    scaledHexWidth() {
      return this.hexWidth * this.scale;
    },
    hexWidthGap() {
      return this.scaledHexWidth + this.gap;
    },
    offsetHalf() {
      return this.hexWidthGap / 2;
    },
    verticalOverlap() {
      return this.hexHeight * 0.25;
    },
    fontSizeNormal() {
      return Math.max(12, Math.floor(16 * this.scale));
    },
    fontSizeNumberNormal() {
      return this.fontSizeNormal * 1.5;
    },
    fontSizeMax() {
      return Math.floor(16 * 1);
    },
    fontSizeNumberMax() {
      return this.fontSizeMax * 1.5;
    },
    rows() {
      const r = [];
      for (let i = 0; i < this.companies.length; i += this.cols) {
        r.push(this.companies.slice(i, i + this.cols));
      }
      return r;
    },
    countCompany() {
      return this.companies.length || 0;
    },
    companyStatus() {
      const statusCount = this.companies.reduce((acc: any, company: any) => {
        let statusValue = -2;

        if (company.dashboard_data?.allModel === 0) {
          statusValue = -2; // No Data
        } else if (company.dashboard_data?.allAlarmModel > 0) {
          statusValue = 2; // Alarm
        } else if (company.dashboard_data?.allWarningModel > 0) {
          statusValue = 1; // Warning
        } else if (company.dashboard_data?.allNormalModel > 0) {
          statusValue = 0; // Normal
        } else if (company.dashboard_data?.allStopModel > 0) {
          statusValue = -1; // Stop Run
        }

        if (statusValue !== null) {
          acc[statusValue] = (acc[statusValue] || 0) + 1;
        }
        return acc;
      }, {});

      return [
        { label: "Normal", value: 0, count: statusCount[0] || 0, alwaysShow: true },
        { label: "Warning", value: 1, count: statusCount[1] || 0, alwaysShow: true },
        { label: "Alarm", value: 2, count: statusCount[2] || 0, alwaysShow: true },
        { label: "Stop Run", value: -1, count: statusCount[-1] || 0, alwaysShow: false },
        { label: "No Data", value: -2, count: statusCount[-2] || 0, alwaysShow: false },
      ].filter((status) => this.showAllStatuses || status.alwaysShow);
    },
  },
  mounted() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.calcLayout();
        this.isInitialized = true;
      });
    });
    window.addEventListener("resize", this.debouncedCalcLayout);
  },
  activated() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.calcLayout();
      });
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.debouncedCalcLayout);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }
  },
  watch: {
    companies: {
      handler() {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.calcLayout();
          });
        });
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    startHoverDelay(rowIndex: number, idx: number) {
      this.cancelHoverDelay();
      this.hoverTimer = setTimeout(() => {
        this.hoverIndex = `${rowIndex}-${idx}`;
      }, 500);
    },
    cancelHoverDelay() {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
      this.hoverIndex = null;
    },
    getHexStyleWithHover(rowIndex: number, idx: number) {
      const baseStyle = this.getHexStyle(rowIndex, idx);
      const isHovering = this.hoverIndex === `${rowIndex}-${idx}`;
      const currentWidth = this.hexWidth * this.scale;
      const targetWidth = this.hexWidth;
      if (isHovering && currentWidth < targetWidth) {
        const enlargeFactor = targetWidth / currentWidth;
        baseStyle.transform = `scale(${enlargeFactor})`;
      } else {
        baseStyle.transform = "scale(1)";
      }
      baseStyle.boxShadow = isHovering ? "0 10px 25px rgba(0,0,0,0.4)" : "0 2px 6px rgba(0,0,0,0.2)";
      baseStyle.zIndex = isHovering ? 10 : 1;
      return baseStyle;
    },
    hoverScaleFactor(rowIndex: number, idx: number) {
      const currentWidth = this.hexWidth * this.scale;
      const targetWidth = this.hexWidth;
      if (this.hoverIndex === `${rowIndex}-${idx}` && currentWidth < targetWidth) {
        return targetWidth / currentWidth; // same factor as container zoom
      }
      return 1;
    },
    hoverFontSize(rowIndex: number, idx: number, baseFontSize: number) {
      if (this.scale > 1) {
        return baseFontSize * this.scale;
      }
      return baseFontSize / this.hoverScaleFactor(rowIndex, idx);
    },
    getColor(overall: number) {
      if (overall === 2) return "bg-[#E74C3C] text-white";
      if (overall === 1) return "bg-[#EDC411] text-black";
      if (overall === 0) return "bg-[#2FCC66] text-white";
      if (overall === -1) return "bg-[#505159] text-white";
      return "bg-[#F2F2F2] text-gray-400";
    },
    debouncedCalcLayout() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.calcLayout();
      }, 150);
    },
    calcLayout() {
      const container = this.$refs.container as HTMLElement;
      if (!container) {
        requestAnimationFrame(() => this.calcLayout());
        return;
      }

      container.offsetHeight; // force reflow

      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      if (containerWidth === 0 || containerHeight === 0) {
        requestAnimationFrame(() => this.calcLayout());
        return;
      }

      const minHexWidth = 10;
      const maxCols = Math.floor(containerWidth / (minHexWidth + this.gap * 2));
      const optimalCols = Math.min(Math.max(1, Math.floor(Math.sqrt(this.companies.length * 1.5))), maxCols);
      this.cols = optimalCols || 1;
      const numRows = Math.ceil(this.companies.length / this.cols);
      const widthPerHex = containerWidth / this.cols;
      const scaleByWidth = (widthPerHex - this.gap * 3) / this.hexWidth;
      const baseHexHeight = (this.hexWidth * 2) / Math.sqrt(3);
      const verticalOverlapRatio = 0.25;
      const totalHeightNeeded = baseHexHeight * (1 + (numRows - 1) * (1 - verticalOverlapRatio));
      const scaleByHeight = containerHeight > 0 ? (containerHeight - this.gap * numRows) / totalHeightNeeded : 1;
      this.scale = Math.min(Math.max(0.1, scaleByWidth), Math.max(0.1, scaleByHeight), 2);
    },
    getRowStyle(rowIndex: number) {
      const row = this.rows[rowIndex];
      const prevRow = this.rows[rowIndex - 1];
      const isEvenRow = rowIndex % 2 === 1;
      const thisLen = row.length;
      const prevLen = prevRow?.length;
      const parityChanged = prevLen && prevLen % 2 !== thisLen % 2;
      const finalOffset =
        isEvenRow || parityChanged ? -this.offsetHalf + (parityChanged ? this.hexWidthGap : 0) : -this.offsetHalf;
      return {
        marginLeft: finalOffset + "px",
        marginTop: rowIndex === 0 ? 0 : `-${this.verticalOverlap}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: this.gap + "px",
      };
    },
    getHexStyle(rowIndex: number, idx: number) {
      const isEvenRow = rowIndex % 2 === 1;
      const isFirst = idx === 0;
      const additionalMargin = isEvenRow && isFirst ? this.offsetHalf * 2 : 0;
      return {
        height: this.hexHeight + "px",
        width: this.scaledHexWidth + "px",
        marginLeft: additionalMargin ? additionalMargin + "px" : undefined,
        clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
      };
    },
    onSelected(company: any) {
      this.$emit("select", company);
    },
  },
};
